import { ErrorHandler, NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './components/login/login.component';
import { SignupComponent } from './components/signup/signup.component';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import { HomeComponent } from './home/home.component';
import { ScreenSizeDetector } from './directives/screen-size-detector.directive';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { UserManagementComponent } from './components/user-management/user-management.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { ScreenHeadingComponent } from './components/screen-heading/screen-heading.component';
import { MatSelectModule } from '@angular/material/select'
import { MatFormFieldModule } from '@angular/material/form-field';
import { TableFilterComponent } from './components/table-filter/table-filter.component';
import { MenuButtonsComponent } from './components/menu-buttons/menu-buttons.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { HasValuePipe } from 'src/pipes/has-value.pipe';
import { MaskStringPipe  } from 'src/pipes/mask.pipe';
import { MatTableModule } from '@angular/material/table';
import { SingleSelectDropdownComponent } from './components/dropdowns/single-select-dropdown/single-select-dropdown.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PrimaryDialogComponent } from './components/primary-dialog/primary-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { AppConfig } from './config/config';
import { BUComponent } from './components/bu/bu.component';
import { UtComponent } from './components/ut/ut.component';
import { GlobalSidemenuComponent } from './components/layout/global-sidemenu/global-sidemenu.component';
import { GlobalHeaderComponent } from './components/layout/global-header/global-header.component';
import { GlobalLayoutComponent } from './components/layout/global-layout/global-layout.component';
import { UserCreateEditComponent } from './components/user-management/user-create-edit/user-create-edit.component';
import { ModalConfirmComponent } from './components/modal-confirm/modal-confirm.component';
import { ApplicationManagementComponent } from './components/application-management/application-management.component';
import { ApplicationCreateEditComponent } from './components/application-management/application-create-edit/application-create-edit.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { HttpInterceptorService } from 'src/helper/interceptor';
import * as Sentry from "@sentry/angular-ivy";
import { NgxUiLoaderModule, NgxUiLoaderRouterModule, NgxUiLoaderHttpModule, NgxUiLoaderConfig, POSITION, SPINNER } from 'ngx-ui-loader';
import { AuditlogManagementComponent } from './components/auditlog-management/auditlog-management.component';
import { SupportFormComponent } from './components/support-form/support-form.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input-gg';
import { TwoFactorDialogComponent } from './components/two-factor-popup/two-factor-dialog.component';
import { TermsAndConditionsComponent } from './components/terms-and-conditions/terms-and-conditions.component';
import { ErrorComponent } from './components/error/error.component';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { UtilitiesCreateEditComponent } from './components/utilities/utilities-create-edit/utilities-create-edit.component';
import { UtilitiesManagementComponent } from './components/utilities/utilities-management.component';
import { MicrosoftConfigService } from './services/helpers/microsoft-config-service';
import { MSALInstanceFactory } from './services/helpers/msalFactory';

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsPosition: POSITION.centerCenter,
  bgsColor: 'var(--primary-color)',
  fgsColor: 'var(--primary-color)',
  overlayColor: 'transparent',
  bgsOpacity: 0.85,
  bgsSize: 70,
  fgsSize: 70,
  fgsType: SPINNER.rectangleBounce,
  bgsType: SPINNER.rectangleBounce,
};
export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([ 
        ['https://graph.microsoft.com./v1.0/me', ['user.read']]
    ])
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: ['user.read'],
      prompt: "select_account"
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SignupComponent,
    HomeComponent,
    ScreenSizeDetector,
    HasValuePipe,
    MaskStringPipe,
    UserManagementComponent,
    ScreenHeadingComponent,
    TableFilterComponent,
    MenuButtonsComponent,
    PaginationComponent,
    SingleSelectDropdownComponent,
    PrimaryDialogComponent,
    BUComponent,
    UtComponent,
    GlobalLayoutComponent,
    GlobalHeaderComponent,
    GlobalSidemenuComponent,
    UserCreateEditComponent,
    ModalConfirmComponent,
    ApplicationManagementComponent,
    ApplicationCreateEditComponent,
    UtilitiesCreateEditComponent,
    UtilitiesManagementComponent,
    ChangePasswordComponent,
    AuditlogManagementComponent,
    SupportFormComponent,
    TwoFactorDialogComponent,
    TermsAndConditionsComponent,
    ErrorComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
    NgxUiLoaderRouterModule.forRoot({ showForeground: true }),
    NgxUiLoaderHttpModule.forRoot({ showForeground: true }),
    //math imports
    MatDialogModule,
    MatMenuModule,
    MatIconModule,
    MatTableModule,
    MatDatepickerModule,
    MatFormFieldModule,
    NgxIntlTelInputModule,
    MatSelectModule,
    MatSidenavModule,
    MsalModule,
  ],
  providers: [
    AppConfig,    
    MicrosoftConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: MicrosoftConfigService) => () => {
        const urlParams = new URLSearchParams(window.location.search);
        console.log('URL Parameters:', Array.from(urlParams.entries())); // Log all URL parameters
        if (urlParams.has('client_id')) {
          return configService.loadBackendConfig();
        }
        return Promise.resolve();
      },
      deps: [MicrosoftConfigService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      })
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [MicrosoftConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
